/**
 * Starts the Algolia autocomplete bundles when a search trigger is hovered,
 * focused, or pressed, so they stay off the initial page load and are ready
 * before the modal opens.
 *
 * Script URLs come from `[data-algolia-autocomplete-loader]` in
 * `sections/predictive-search.liquid`.
 */

class AlgoliaAutocompleteLoader {
  static #TRIGGER_SELECTOR = '[aria-controls="algolia-search-modal"]';
  static #CONFIG_SELECTOR = '[data-algolia-autocomplete-loader]';

  /** @type {string[]} */
  #sources = [];

  #started = false;

  #onIntent = (event) => {
    if (!this.#isSearchTrigger(event)) return;
    this.#loadOnce();
  };

  constructor() {
    this.#sources = this.#readScriptSources();
    if (this.#sources.length === 0) return;

    document.addEventListener('pointerenter', this.#onIntent, true);
    document.addEventListener('focusin', this.#onIntent);
    document.addEventListener('pointerdown', this.#onIntent, true);
  }

  /**
   * @returns {string[]}
   */
  #readScriptSources() {
    const configEl = document.querySelector(AlgoliaAutocompleteLoader.#CONFIG_SELECTOR);
    if (!configEl?.textContent) return [];

    try {
      const parsed = JSON.parse(configEl.textContent);
      if (!Array.isArray(parsed.scripts)) return [];

      return parsed.scripts.filter((src) => typeof src === 'string' && src !== '');
    } catch {
      return [];
    }
  }

  #loadScripts() {
    for (const src of this.#sources) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.dataset.cookieconsent = 'ignore';
      document.head.append(script);
    }
  }

  #loadOnce() {
    if (this.#started) return;
    this.#started = true;
    this.#loadScripts();
  }

  /**
   * @param {Event} event
   */
  #isSearchTrigger(event) {
    const { target } = event;
    return (
      target instanceof Element &&
      target.closest(AlgoliaAutocompleteLoader.#TRIGGER_SELECTOR) !== null
    );
  }
}

new AlgoliaAutocompleteLoader();
